import { useApiQuery, type Api } from "../../../services/api";

const MIN_QUERY_LENGTH = 3;
const MIN_ARTICLES = 5;
// Mirrors the server-side cap in key-facts.ts. The synthesis is corpus-
// wide so we pass every article the search returned (one page typically).
const MAX_ARTICLES = 50;

export type KeyFact =
  Api["get"]["/webapp/public/articles/key-facts"]["output"]["body"]["facts"][number];

export interface KeyFactsState {
  facts: readonly KeyFact[];
  isLoading: boolean;
}

const EMPTY: KeyFactsState = { facts: [], isLoading: false };

export function useSearchKeyFactsQuery(
  searchValue: string,
  articleIds: readonly string[]
): KeyFactsState {
  const trimmed = searchValue.trim();
  // Cap the top-N corpus we ship to the LLM. The server enforces the same
  // cap defensively (MAX_ARTICLES on the endpoint).
  const topIds = articleIds.slice(0, MAX_ARTICLES);

  const isEnabled =
    trimmed.length >= MIN_QUERY_LENGTH && topIds.length >= MIN_ARTICLES;

  const query = useApiQuery(
    "/webapp/public/articles/key-facts",
    isEnabled
      ? {
          query: { q: trimmed, articleIds: topIds },
          // Don't re-trigger on every keystroke once we've settled on a
          // corpus — this is an expensive synthesis, the server cache will
          // catch duplicates anyway.
          staleTime: 60_000,
        }
      : false
  );

  if (query.state === "success") {
    return { facts: query.body.facts, isLoading: false };
  }
  if (query.state === "pending") {
    return { facts: [], isLoading: true };
  }
  return EMPTY;
}
